import { downloadFileContentFromStorage, isStorageBackedFile } from '../services/projects';
import { ProjectFile } from '../types';

const ZIP_MADE_BY_VERSION = 20;
const ZIP_REQUIRED_VERSION = 20;
const ZIP_UTF8_FLAG = 0x0800;
const ZIP_STORE_METHOD = 0;
const LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50;
const CENTRAL_DIRECTORY_HEADER_SIGNATURE = 0x02014b50;
const END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const DIRECTORY_EXTERNAL_ATTRIBUTES = 0x10 << 16;
const FILE_EXTERNAL_ATTRIBUTES = 0x20 << 16;

interface ZipEntry {
  path: string;
  bytes: Uint8Array;
  directory: boolean;
}

export interface ProjectZipAdditionalEntry {
  path: string;
  bytes: Uint8Array;
}

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let index = 0; index < 256; index += 1) {
    let value = index;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) === 1
        ? 0xedb88320 ^ (value >>> 1)
        : value >>> 1;
    }
    table[index] = value >>> 0;
  }
  return table;
})();

function stripControlCharacters(value: string): string {
  let result = '';

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character.charCodeAt(0) >= 32) {
      result += character;
    }
  }

  return result;
}

function encodeUtf8(value: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value);
  }

  const encoded = encodeURIComponent(value).replace(
    /%([0-9A-F]{2})/g,
    (_, hex: string) => String.fromCharCode(parseInt(hex, 16))
  );
  const bytes = new Uint8Array(encoded.length);

  for (let index = 0; index < encoded.length; index += 1) {
    bytes[index] = encoded.charCodeAt(index);
  }

  return bytes;
}

function sanitizeArchiveSegment(name: string): string {
  const normalized = name
    .trim()
    .replace(/[\\/]+/g, '-');
  const cleaned = stripControlCharacters(normalized)
    .replace(/^\.+$/, '');

  return cleaned || 'untitled';
}

function appendDuplicateSuffix(name: string, duplicateIndex: number): string {
  const extensionIndex = name.lastIndexOf('.');
  if (extensionIndex > 0) {
    return `${name.slice(0, extensionIndex)} (${duplicateIndex})${name.slice(extensionIndex)}`;
  }
  return `${name} (${duplicateIndex})`;
}

function getEffectiveParentId(file: ProjectFile, filesById: Map<string, ProjectFile>): string | null {
  if (!file.parentId) {
    return null;
  }

  const parent = filesById.get(file.parentId);
  return parent?.type === 'folder' ? parent.id : null;
}

function getDosDateTime(value: Date): { time: number; date: number } {
  const year = Math.max(1980, value.getFullYear());
  const month = value.getMonth() + 1;
  const day = value.getDate();
  const hours = value.getHours();
  const minutes = value.getMinutes();
  const seconds = Math.floor(value.getSeconds() / 2);

  return {
    time: (hours << 11) | (minutes << 5) | seconds,
    date: ((year - 1980) << 9) | (month << 5) | day,
  };
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    const value = bytes[index];
    crc = crcTable[(crc ^ value) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function buildFileHeader(
  signature: number,
  nameBytes: Uint8Array,
  payload: Uint8Array,
  crc: number,
  headerOffset: number,
  directory: boolean,
  time: number,
  date: number
): Uint8Array {
  const header = new Uint8Array(signature === LOCAL_FILE_HEADER_SIGNATURE
    ? 30 + nameBytes.length
    : 46 + nameBytes.length);
  const view = new DataView(header.buffer);

  if (signature === LOCAL_FILE_HEADER_SIGNATURE) {
    view.setUint32(0, LOCAL_FILE_HEADER_SIGNATURE, true);
    view.setUint16(4, ZIP_REQUIRED_VERSION, true);
    view.setUint16(6, ZIP_UTF8_FLAG, true);
    view.setUint16(8, ZIP_STORE_METHOD, true);
    view.setUint16(10, time, true);
    view.setUint16(12, date, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, payload.length, true);
    view.setUint32(22, payload.length, true);
    view.setUint16(26, nameBytes.length, true);
    view.setUint16(28, 0, true);
    header.set(nameBytes, 30);
    return header;
  }

  view.setUint32(0, CENTRAL_DIRECTORY_HEADER_SIGNATURE, true);
  view.setUint16(4, ZIP_MADE_BY_VERSION, true);
  view.setUint16(6, ZIP_REQUIRED_VERSION, true);
  view.setUint16(8, ZIP_UTF8_FLAG, true);
  view.setUint16(10, ZIP_STORE_METHOD, true);
  view.setUint16(12, time, true);
  view.setUint16(14, date, true);
  view.setUint32(16, crc, true);
  view.setUint32(20, payload.length, true);
  view.setUint32(24, payload.length, true);
  view.setUint16(28, nameBytes.length, true);
  view.setUint16(30, 0, true);
  view.setUint16(32, 0, true);
  view.setUint16(34, 0, true);
  view.setUint16(36, 0, true);
  view.setUint32(38, directory ? DIRECTORY_EXTERNAL_ATTRIBUTES : FILE_EXTERNAL_ATTRIBUTES, true);
  view.setUint32(42, headerOffset, true);
  header.set(nameBytes, 46);
  return header;
}

async function loadFileBytes(file: ProjectFile): Promise<Uint8Array> {
  if (file.type === 'folder') {
    return new Uint8Array();
  }

  if (isStorageBackedFile(file) && file.storageContentPath) {
    const content = await downloadFileContentFromStorage(file.storageContentPath);
    return encodeUtf8(content);
  }

  if (file.type === 'image') {
    const response = await fetch(file.content);
    if (!response.ok) {
      throw new Error(`Failed to download image "${file.name}" (${response.status})`);
    }
    return new Uint8Array(await response.arrayBuffer());
  }

  return encodeUtf8(file.content ?? '');
}

async function buildArchiveEntries(
  files: ProjectFile[],
  additionalEntries: ProjectZipAdditionalEntry[] = []
): Promise<ZipEntry[]> {
  const filesById = new Map(files.map((file) => [file.id, file]));
  const childrenByParent = new Map<string | null, ProjectFile[]>();

  files.forEach((file) => {
    const parentId = getEffectiveParentId(file, filesById);
    const siblings = childrenByParent.get(parentId) ?? [];
    siblings.push(file);
    childrenByParent.set(parentId, siblings);
  });

  const segmentById = new Map<string, string>();
  childrenByParent.forEach((siblings) => {
    const seenNames = new Map<string, number>();

    siblings.forEach((file) => {
      const baseName = sanitizeArchiveSegment(file.name);
      const normalizedKey = baseName.toLocaleLowerCase();
      const nextCount = (seenNames.get(normalizedKey) ?? 0) + 1;
      seenNames.set(normalizedKey, nextCount);
      segmentById.set(
        file.id,
        nextCount === 1 ? baseName : appendDuplicateSuffix(baseName, nextCount)
      );
    });
  });

  const pathCache = new Map<string, string>();

  function resolveArchivePath(fileId: string, stack = new Set<string>()): string {
    const cached = pathCache.get(fileId);
    if (cached) {
      return cached;
    }

    const segment = segmentById.get(fileId) ?? 'untitled';
    if (stack.has(fileId)) {
      return segment;
    }

    stack.add(fileId);
    const file = filesById.get(fileId);
    const parentId = file ? getEffectiveParentId(file, filesById) : null;
    const path = parentId
      ? `${resolveArchivePath(parentId, stack).replace(/\/$/, '')}/${segment}`
      : segment;
    stack.delete(fileId);

    pathCache.set(fileId, path);
    return path;
  }

  const directoryEntries: ZipEntry[] = [];
  const fileEntries: Array<Promise<ZipEntry>> = [];

  files.forEach((file) => {
    const archivePath = resolveArchivePath(file.id);

    if (file.type === 'folder') {
      directoryEntries.push({
        path: `${archivePath.replace(/\/$/, '')}/`,
        bytes: new Uint8Array(),
        directory: true,
      });
      return;
    }

    fileEntries.push(
      loadFileBytes(file).then((bytes) => ({
        path: archivePath,
        bytes,
        directory: false,
      }))
    );
  });

  const sortedDirectories = [...directoryEntries].sort((left, right) => {
    const depthDifference = left.path.split('/').length - right.path.split('/').length;
    return depthDifference !== 0 ? depthDifference : left.path.localeCompare(right.path);
  });

  const extraFileEntries = additionalEntries.map((entry) => ({
    path: entry.path.replace(/^\/+/, ''),
    bytes: entry.bytes,
    directory: false,
  }));

  return [...sortedDirectories, ...(await Promise.all(fileEntries)), ...extraFileEntries];
}

export async function buildProjectZipBlob(
  files: ProjectFile[],
  additionalEntries: ProjectZipAdditionalEntry[] = []
): Promise<Blob> {
  const entries = await buildArchiveEntries(files, additionalEntries);
  const localParts: Uint8Array[] = [];
  const centralDirectoryParts: Uint8Array[] = [];
  const now = getDosDateTime(new Date());
  let offset = 0;
  let centralDirectorySize = 0;

  entries.forEach((entry) => {
    const nameBytes = encodeUtf8(entry.path);
    const payload = entry.bytes;
    const checksum = entry.directory ? 0 : crc32(payload);
    const localHeader = buildFileHeader(
      LOCAL_FILE_HEADER_SIGNATURE,
      nameBytes,
      payload,
      checksum,
      0,
      entry.directory,
      now.time,
      now.date
    );
    const centralDirectoryHeader = buildFileHeader(
      CENTRAL_DIRECTORY_HEADER_SIGNATURE,
      nameBytes,
      payload,
      checksum,
      offset,
      entry.directory,
      now.time,
      now.date
    );

    localParts.push(localHeader, payload);
    centralDirectoryParts.push(centralDirectoryHeader);
    offset += localHeader.length + payload.length;
    centralDirectorySize += centralDirectoryHeader.length;
  });

  const endOfCentralDirectory = new Uint8Array(22);
  const view = new DataView(endOfCentralDirectory.buffer);
  view.setUint32(0, END_OF_CENTRAL_DIRECTORY_SIGNATURE, true);
  view.setUint16(4, 0, true);
  view.setUint16(6, 0, true);
  view.setUint16(8, entries.length, true);
  view.setUint16(10, entries.length, true);
  view.setUint32(12, centralDirectorySize, true);
  view.setUint32(16, offset, true);
  view.setUint16(20, 0, true);

  return new Blob([...localParts, ...centralDirectoryParts, endOfCentralDirectory], {
    type: 'application/zip',
  });
}

function getArchiveFileName(projectTitle: string): string {
  const baseName = sanitizeArchiveSegment(projectTitle)
    .replace(/[<>:"|?*]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

  return `${baseName || 'project'}.zip`;
}

export async function downloadProjectZip(
  projectTitle: string,
  files: ProjectFile[],
  options?: { additionalEntries?: ProjectZipAdditionalEntry[] }
): Promise<void> {
  const archive = await buildProjectZipBlob(files, options?.additionalEntries);
  const archiveUrl = URL.createObjectURL(archive);
  const anchor = document.createElement('a');

  anchor.href = archiveUrl;
  anchor.download = getArchiveFileName(projectTitle);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  window.setTimeout(() => {
    URL.revokeObjectURL(archiveUrl);
  }, 0);
}
